import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SEO } from "@/components/SEO";
import { packagesPageSchema, localBusinessSchema, generateBreadcrumbSchema } from "@/lib/structuredData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, Download, Mail, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const packages = [
  {
    id: "basic",
    name: "Basic Package",
    price: "R300",
    period: "p/m",
    turnover: "Nil Turnover",
    popular: false,
    features: [
      "Nil Annual Tax Return",
      "2x Nil Provisional Tax Return",
      "CIPC Annual Return (Nil Turnover)",
      "Beneficial Ownership",
    ],
  },
  {
    id: "entry",
    name: "Entry Level Package",
    price: "R2,500",
    period: "p/m",
    turnover: "Turnover up to R2.3m",
    popular: true,
    features: [
      "Monthly Bookkeeping up to 60 Entries",
      "Payroll 1-5 Employees",
      "EMP201 Monthly Declaration",
      "EMP501 Bi-Annual Reconciliation",
      "UIF Monthly Declaration",
      "Annual Financial Statements",
      "Annual Tax Return",
      "Provisional Tax (2 Returns)",
      "Beneficial Ownership",
      "CIPC Annual Return",
    ],
  },
  {
    id: "medium",
    name: "Medium Level Package",
    price: "R4,500",
    period: "p/m",
    turnover: "Turnover up to R12m",
    popular: false,
    features: [
      "Monthly Bookkeeping up to 120 Entries",
      "VAT201 Monthly Declaration",
      "Payroll 1-10 Employees",
      "EMP201 Monthly Declaration",
      "EMP501 Bi-Annual Reconciliation",
      "UIF Monthly Declaration",
      "Workmen's Compensation Return",
      "Annual Financial Statements",
      "Annual Tax Return",
      "Provisional Tax (2 Returns)",
      "Beneficial Ownership",
      "CIPC Annual Return",
    ],
  },
];

const Packages = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  // Handle URL query parameter for package enquiry (from home page)
  useEffect(() => {
    const enquirePackage = searchParams.get("enquire");
    if (enquirePackage) {
      setSelectedPackage(enquirePackage);
      setTimeout(() => {
        document.getElementById("enquiry-form")?.scrollIntoView({ 
          behavior: "smooth" 
        });
      }, 100);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          formType: "package",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          selectedPackage: selectedPackage || "No specific package selected",
        },
      });

      if (error) throw error;

      toast({
        title: "Enquiry Submitted!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      setSelectedPackage("");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Failed to submit enquiry. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO 
        title="Bookkeeping Packages from R300/month | All-Inclusive Pricing"
        description="No hidden fees. SAICA registered bookkeeping from R300/month — includes payroll, tax returns, EMP201, CIPC & SARS compliance. Compare packages and get started today."
        canonical="/packages"
        structuredData={[packagesPageSchema, localBusinessSchema, generateBreadcrumbSchema([
          { name: "Home", url: "https://bookkeepingservices.co.za/" },
          { name: "Packages & Pricing", url: "https://bookkeepingservices.co.za/packages" }
        ])]}
      />
      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Packages & Pricing
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Transparent, all-inclusive packages designed for South African businesses. 
              Choose the plan that fits your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={cn(
                  "card-elevated p-8 flex flex-col",
                  pkg.popular && "card-popular"
                )}
              >
                {/* Package Header */}
                <div className="text-center mb-6">
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">{pkg.turnover}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="price-amount">{pkg.price}</span>
                    <span className="price-period">{pkg.period}</span>
                  </div>
                  
                </div>

                {/* Divider */}
                <div className="h-px bg-border my-6" />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  className={cn(
                    "w-full",
                    pkg.popular ? "btn-primary" : "btn-outline-primary"
                  )}
                  onClick={() => {
                    setSelectedPackage(pkg.name);
                    requestAnimationFrame(() => {
                      document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" });
                    });
                  }}
                >
                  Enquire: {pkg.name.replace(" Package", "")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Quote Form */}
      <section id="enquiry-form" className="py-20 lg:py-28 section-light">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Get in Touch"
            title="Need a Custom Quote?"
            description="Tell us about your business and we'll create a tailored solution for you."
          />

          <div className="max-w-2xl mx-auto">
            {/* Contact Info */}
            <div className="bg-accent rounded-xl p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <span className="font-medium">info@bookkeepingservices.co.za</span>
              </div>
              <a href="/BOOKKEEPING_SERVICES_SA_packages.pdf" download>
                <Button variant="outline" className="btn-outline-primary gap-2">
                  <Download className="w-4 h-4" />
                  Download Packages PDF
                </Button>
              </a>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-8 shadow-soft">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+27 12 345 6789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your company name"
                  />
                </div>
              </div>

              {selectedPackage && (
                <div className="mb-6 p-4 bg-accent rounded-lg">
                  <p className="text-sm text-muted-foreground">Enquiring about:</p>
                  <p className="font-semibold text-primary">{selectedPackage}</p>
                </div>
              )}

              <div className="space-y-2 mb-6">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your business and what you're looking for..."
                />
              </div>

              <p className="text-xs text-muted-foreground mb-6">
                Your information is kept confidential in accordance with POPIA.
              </p>

              <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Enquiry"
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Packages;
